import { Index } from 'flexsearch';
import { Temporal } from 'temporal-polyfill';
import type { LocaleType } from '@/models/common/locale.ts';
import type { Lead, LeadId } from '@/models/entities/lead';
import { LeadLifecycleStatus } from '@/models/entities/lead';
import {
  contactabilityFromLead,
  urgencyFromLead,
} from '@/models/entities/workflowExtensions';
import type {
  ChipStatus,
  ContactabilityFilter,
  ContactabilityFilterCount,
  FilterChipCount,
  UrgencyFilter,
  UrgencyFilterCount,
} from '@/models/ui/filterChip';
import {
  ContactabilityFilterCountSchema,
  ContactabilityFilterEnum,
  FilterChipCountSchema,
  UrgencyFilterCountSchema,
  UrgencyFilterEnum,
} from '@/models/ui/filterChip';
import type { LeadFilters } from '@/models/ui/leadFilters';
import { LeadStatusGroups } from '@/models/ui/leadStatusGroup.ts';
import {
  mapLeadToSearchModel,
  type SearchQuery,
  SearchQueryNone,
} from '@/models/ui/search';

export interface LeadCollectionCounts {
  inProgress: number;
  unknownCustomer: number;
  lead: number;
  overdue: number;
  overdueSoon: number;
  scheduled: number;
  inRepair: number;
  executed: number;
  archive: number;
}

export class LeadCollection {
  private readonly searchIndex: Index;
  private readonly leads: Lead[];
  readonly locale: LocaleType;

  constructor(leads: Lead[] | readonly Lead[], locale: LocaleType) {
    this.leads = [...leads];
    this.locale = locale;
    const searchModels = leads.map((l) => mapLeadToSearchModel(l, locale));

    this.searchIndex = new Index({
      tokenize: 'forward',
      encoder: 'Default',
      cache: true,
      resolution: 9,
    });

    for (const model of searchModels) {
      const content = [
        model.customerName,
        model.contactName,
        model.phoneNumber,
        model.vin,
        model.dealerName,
        model.status,
        model.maintenanceItems,
      ]
        .filter(Boolean)
        .join(' ');
      this.searchIndex.add(model.leadId, content);
    }
  }

  private searchLeadIds(search: SearchQuery): Set<LeadId> {
    const searchResults = this.searchIndex.search(search, {
      limit: this.leads.length,
    });
    return new Set(searchResults as LeadId[]);
  }

  private static compareLeads(a: Lead, b: Lead): number {
    const dueDateCompare = Temporal.PlainDate.compare(
      a.maintenance.dueDate,
      b.maintenance.dueDate,
    );
    if (dueDateCompare !== 0) return dueDateCompare;

    const createdAtCompare = Temporal.Instant.compare(
      a.workflow.createdAt,
      b.workflow.createdAt,
    );
    if (createdAtCompare !== 0) return createdAtCompare;

    return a.id.localeCompare(b.id);
  }

  get counts(): LeadCollectionCounts {
    const initialCounts: LeadCollectionCounts = {
      inProgress: 0,
      unknownCustomer: 0,
      lead: 0,
      overdue: 0,
      overdueSoon: 0,
      scheduled: 0,
      inRepair: 0,
      executed: 0,
      archive: 0,
    };

    return this.leads.reduce((acc, lead) => {
      if (lead.workflow.statusGroup === LeadStatusGroups.InProgress) {
        acc.inProgress++;
      } else if (lead.workflow.statusGroup === LeadStatusGroups.Archive) {
        acc.archive++;
      }

      if (lead.customer.isUnknown) {
        acc.unknownCustomer++;
      }

      const { status } = lead.workflow;
      if (status === LeadLifecycleStatus.Lead) acc.lead++;
      else if (status === LeadLifecycleStatus.Scheduled) acc.scheduled++;
      else if (status === LeadLifecycleStatus.InRepair) acc.inRepair++;
      else if (status === LeadLifecycleStatus.Executed) acc.executed++;

      const urgency = urgencyFromLead(lead);
      if (urgency === UrgencyFilterEnum.enum.Overdue) acc.overdue++;
      else if (urgency === UrgencyFilterEnum.enum.OverdueSoon) {
        acc.overdueSoon++;
      }

      return acc;
    }, initialCounts);
  }

  chipCounts(_activeStatuses: ChipStatus[] = []): FilterChipCount[] {
    const { lead, scheduled, inRepair, executed } = this.counts;
    return [
      { status: LeadLifecycleStatus.Lead, count: lead },
      { status: LeadLifecycleStatus.Scheduled, count: scheduled },
      { status: LeadLifecycleStatus.InRepair, count: inRepair },
      { status: LeadLifecycleStatus.Executed, count: executed },
    ].map((c) => FilterChipCountSchema.parse(c));
  }

  urgencyCounts(_activeUrgencies: UrgencyFilter[] = []): UrgencyFilterCount[] {
    const counts = this.leads.reduce(
      (acc, lead) => {
        if (lead.workflow.statusGroup === LeadStatusGroups.Archive) {
          return acc;
        }
        const urgency = urgencyFromLead(lead);
        if (urgency === UrgencyFilterEnum.enum.Overdue) {
          acc.overdue++;
        } else if (urgency === UrgencyFilterEnum.enum.OverdueSoon) {
          acc.overdueSoon++;
        }
        return acc;
      },
      { overdue: 0, overdueSoon: 0 },
    );

    return [
      { urgency: UrgencyFilterEnum.enum.Overdue, count: counts.overdue },
      {
        urgency: UrgencyFilterEnum.enum.OverdueSoon,
        count: counts.overdueSoon,
      },
    ].map((c) => UrgencyFilterCountSchema.parse(c));
  }

  contactabilityCounts(
    _activeContactabilities: ContactabilityFilter[] = [],
  ): ContactabilityFilterCount[] {
    const counts = this.leads.reduce(
      (acc, lead) => {
        if (lead.workflow.statusGroup === LeadStatusGroups.Archive) {
          return acc;
        }

        const contactability = contactabilityFromLead(lead);
        if (contactability === ContactabilityFilterEnum.enum.Unreachable) {
          acc.unreachable++;
        }

        return acc;
      },
      { unreachable: 0 },
    );

    return [
      {
        contactability: ContactabilityFilterEnum.enum.Unreachable,
        count: counts.unreachable,
      },
    ].map((c) => ContactabilityFilterCountSchema.parse(c));
  }

  filter(filters: LeadFilters): Lead[] {
    const { statusGroup, statuses, urgencies, contactabilities, search } =
      filters;
    let filtered = [...this.leads];

    if (statusGroup) {
      filtered = filtered.filter(
        (lead) => lead.workflow.statusGroup === statusGroup,
      );
    }

    // Status logic
    if (
      statusGroup !== LeadStatusGroups.Archive &&
      statuses &&
      statuses.length > 0
    ) {
      filtered = filtered.filter((lead) =>
        statuses.includes(lead.workflow.status),
      );
    }

    if (
      statusGroup !== LeadStatusGroups.Archive &&
      urgencies &&
      urgencies.length > 0
    ) {
      filtered = filtered.filter((lead) => {
        const urgency = urgencyFromLead(lead);
        return urgency !== null && urgencies.includes(urgency);
      });
    }

    if (
      statusGroup !== LeadStatusGroups.Archive &&
      contactabilities &&
      contactabilities.length > 0
    ) {
      filtered = filtered.filter((lead) => {
        const contactability = contactabilityFromLead(lead);
        return (
          contactability !== null && contactabilities.includes(contactability)
        );
      });
    }

    // Search logic
    if (search && search !== SearchQueryNone) {
      const resultIds = this.searchLeadIds(search);
      filtered = filtered.filter((lead) => resultIds.has(lead.id));
    }

    return filtered.sort(LeadCollection.compareLeads);
  }

  getLead(leadId: LeadId): Lead | null {
    const lead = this.leads.find((l) => l.id === leadId);
    if (!lead) {
      return null;
    }
    return lead;
  }
}
